/**
 * Renders an assessment card
 * @prop {string} id - Unique id for ARIA & HTML attributes
 * @property {object} assessment - The assessment’s data
 */

import React from 'react';
import { Link } from 'react-router-dom';

// Translation
import translate from '../translate/Translate';

// Icons
import * as icons from '../ui/Icons';

const AssessmentCard = class AssessmentCard extends React.Component {
  constructor() {
    super();

    // Set the initial state
    this.state = {
      status: 'new'
    };
  }

  componentDidMount() {
    this.handleStatus();
  }

  /**
   * handleStatus - Determines the status of an assessment
   */
  handleStatus() {
    const { assessment } = this.props;
    let status = 'new';

    if (
      assessment.completedAttemptCount &&
      (!assessment.activeAttempt || !assessment.activeAttempt.sid)
    ) {
      status = 'completed';
    } else if (assessment.activeAttempt && assessment.activeAttempt.sid) {
      status = 'in progress';
    }

    if (status !== this.state.status) {
      this.setState({
        status
      });
    }
  }

  /**
   * renderIcon - Renders the appropriate icon for a given string value
   * @param {string} iconName
   */
  renderIcon = iconName => {
    let icon = <icons.assessmentBook />;

    switch (iconName) {
      case 'book':
        icon = <icons.assessmentBook />;
        break;

      case 'calendar':
        icon = <icons.assessmentCalendar />;
        break;

      case 'computer':
        icon = <icons.assessmentComputer />;
        break;

      case 'light bulb':
        icon = <icons.assessmentLightBulb />;
        break;

      case 'student':
        icon = <icons.assessmentPerson />;
        break;

      case 'typing':
        icon = <icons.assessmentTyping />;
        break;

      case 'writing':
        icon = <icons.assessmentWriting />;
        break;

      case 'math':
        icon = <icons.assessmentMath />;
        break;

      case 'career':
        icon = <icons.assessmentCareer />;
        break;

      default:
        break;
    }

    return icon;
  };

  render() {
    const { allCompleted, assessment, id, translation } = this.props;
    const { status } = this.state;

    return (
      <article className="assessment-card" aria-labelledby={`${id}-heading`}>
        <i className="assessment-card__icon">{this.renderIcon(assessment.iconName)}</i>

        <div className="assessment-card__content">
          <h3 id={`${id}-heading`} className="assessment-card__title">{assessment.name}</h3>

          {assessment.details && <p>{assessment.details}</p>}
        </div>

        <footer className="assessment-card__action">
          {/*
            Assessment has been completed and there are
            no active attempts for this assessment - show completed status
          */}
          {status === 'completed' &&
            (!assessment.activeAttempt || !assessment.activeAttempt.sid) && (
              <span
                className={`status${
                  assessment.status
                    ? ` status--${assessment.status.toLowerCase().replace(' ', '-')}`
                    : ''
                }`}
              >
                <i className="status__icon icon--sm">
                  <icons.checkCircle />
                </i>

                {translation.completed}
              </span>
            )}

          {/*
            All assessments have been completed and there are
            no active attempts for this assessment - show retake button
          */}
          {allCompleted && (!assessment.activeAttempt || !assessment.activeAttempt.sid) && (
            <Link
              to={`/assessment/${assessment.assessmentSid}?groupItem=${assessment.sid}`}
              className="btn btn--ghost"
            >
              <i className="status__icon icon--sm icon--round icon--dark icon--accent">
                <icons.undoCircle />
              </i>

              {translation.retake}
            </Link>
          )}

          {/*
            There is an active attempt for this assessment - show continue button
          */}
          {status === 'in progress' && (
            <Link
              to={`/assessment/${assessment.assessmentSid}?attempt=${assessment.activeAttempt.sid}`}
              className="btn"
            >
              {translation.continue}

              <i className="icon--xs icon--after">
                <icons.chevronRight />
              </i>
            </Link>
          )}

          {/*
            No active attempts for this assessment, and
            not all assessments have been completed - show start button
          */}
          {status === 'new' && (
            <Link
              to={`/assessment/${assessment.assessmentSid}?groupItem=${assessment.sid}`}
              className="btn"
            >
              {translation.start}

              <i className="icon--xs icon--after">
                <icons.chevronRight />
              </i>
            </Link>
          )}
        </footer>
      </article>
    );
  }
};

export default translate('AssessmentCard')(AssessmentCard);
